/**
 * UnifiedTeamSection — the single "Team & Resources" section on the project detail tab.
 *
 * Replaces `projects/TeamSection.tsx` + `ResourceSection.tsx`, which were two disconnected cards
 * backed by two different data sources: `TeamSection` wrote free-text strings onto
 * `projects.project_manager` / `solution_architect` / `engineers_assigned` via `UserSelect`
 * (Cognito users, with a freeform text fallback), while `ResourceSection` created
 * `project_resources` rows via `ResourcePicker` (Clockify directory). A person could appear in
 * both with no deduplication, and the two pickers offered different people.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ONE BACKEND, SEVEN SECTIONS
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * Every section — including the named leads — now reads and writes `project_resources` through
 * `GET/POST/DELETE /api/projects/:id/resources`. This is possible because V043 (F) seeded
 * `Leadership`, `Executive` and `Lead Implementation Engineer` into `project_role_catalog`,
 * lifting the UI design's BE-1/BE-2 blocks (CR §8.3). `UserSelect` and its freeform text fallback
 * are gone from this surface — a resource is always a real directory row, never a typed string.
 *
 * (`Executive` and `Lead Implementation Engineer` are the stored enum values; they are DISPLAYED
 * as "Account Executive" and "Implementation Lead" via `projectRoleLabel()` — see team-config.ts.)
 *
 * ⚠️ KNOWN GAP — `projects.*` CACHE COLUMNS GO STALE ON A UI WRITE.
 * `projects.project_manager` / `solution_architect` / `engineers_assigned` are now DERIVED cache
 * columns (CR §6 Phase 3). Refreshing them from `project_resources` is the job of the backend
 * dual-write service (`packages/resources/services/team-cache.service.ts`,
 * `refreshProjectTeamCache`), which **does not exist yet** — grep confirms no such file. Until it
 * ships, an assignment made here updates `project_resources` but NOT the cache columns, so the
 * three notification readers and `ProjectTeamCard` (which read the cache) will lag.
 *
 * This is deliberately NOT worked around in the frontend. Writing both backends from here is
 * exactly the dual-identity problem BE-3 describes and would re-create the drift this component
 * exists to remove. The fix belongs behind the single `POST /api/projects/:id/resources` endpoint.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * NO CAPACITY ALLOCATION (out of MVP scope — Task #10)
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * This surface neither collects nor displays `capacity_pct`. Every assignment POSTs
 * `capacity_pct: null` (nullable since V043 (C)), which the backend accepts for every role and
 * which skips the server-side overload guard. Never send `capacity_pct ?? 100` or `Number('')`:
 * the first consumes the person's whole cross-project budget (R-4 / R-10), the second is `0` and
 * fails the Zod `.gt(0)` check with a 400.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * GUARDED TEAM CHANGES (T-29 / T-31)
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * Rows are read-only; each row whose role is in the active catalog carries a **Swap** action
 * (`SwapResourceDialog` → `POST /api/projects/:id/resources/swap`, reason required, both people
 * notified). Per-section add/remove behaviour is declared in `team-config.ts` (`addMode`,
 * `removable`): Leadership / Account Executive have no add action; SA / PM / Implementation Lead
 * offer "Assign" only while empty; Engineers / Other keep "Add" behind a confirmation and keep
 * per-row Remove. The "Other" role selector offers only roles WITHOUT their own section, so it
 * cannot be used to bypass those rules.
 *
 * Source: docs/phase2/team-resources-unified-ui-design.md; CR §3.2, §7.1, §7.2, §8.3;
 * docs/phase2/change-requests/2026-10-19-demo-feedback-implementation-plan.md §4.
 */

import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Users, Plus, Trash2, Loader2, ArrowLeftRight } from "lucide-react";
import type { AxiosError } from "axios";

import { useApiClient } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  MISSING_NAME_HINT,
  resolveResourceName,
  type ResourceDisplayName,
} from "@/lib/resource-name";
import {
  PROJECT_ROLE_VALUES,
  DEFAULT_PROJECT_ROLE,
  isNamedLeadRole,
  projectRoleLabel,
} from "@/types/intake";
import { ResourcePicker } from "@/components/resources/ResourcePicker";
import { useAuth } from "@/contexts/AuthContext";
import {
  ROLE_SECTIONS,
  SECTIONED_ROLE_KEYS,
  sectionIdForRole,
  type RoleSection,
} from "@/components/projects/team-config";
import { SwapResourceDialog } from "@/components/projects/SwapResourceDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// ─── Types (mirror packages/shared/types/resources.ts §2.4) ────────────────────
// Duplicated intentionally: the frontend is a standalone vite workspace with no path/dependency to
// packages/shared today (tsconfig `paths` only maps `@/*`), same convention as MeetingIntelligenceCard.

interface Resource {
  id: string;
  name: string;
  email: string | null;
  clockify_user_id: string | null;
  role_title: string | null;
  designation: string | null;
  is_active: boolean;
  created_at: string;
}

interface ProjectResource {
  id: string;
  project_id: string;
  resource_id: string;
  /** Nullable since V043 (C). Not rendered — capacity allocation is out of MVP scope (Task #10). */
  capacity_pct: number | null;
  /** NOT NULL DEFAULT 'Other' since V043 (D). Not enum-narrowed — 4 legacy values persist (CR §8.5). */
  role_on_project: string;
  start_date: string | null;
  end_date: string | null;
  resource?: Resource;
  /**
   * T-32 (V053) — all-time hours this person logged on the project's Clockify project, refreshed
   * daily. null/absent = no Clockify time recorded (or not synced yet). Per PERSON, so a person
   * with two role rows shows the same figure on both.
   */
  clockify_hours?: number | null;
}

export interface UnifiedTeamSectionProps {
  projectId: string;
}

/**
 * Roles that may REMOVE a team member (DELETE /api/projects/:id/resources/:resourceId).
 *
 * Carried over unchanged from `ResourceSection`. Note this is WIDER than `TeamSection`'s old
 * `EDIT_ROLES = ['pm','leadership','admin']`, which excluded `sa`. Unifying on the resource gate
 * is correct: the write now goes to `POST /api/projects/:id/resources`, whose backend RBAC admits
 * `sa` — so the old narrower frontend gate was hiding a control the API would have accepted.
 * The backend re-enforces this list regardless; hiding controls is UX, not security.
 */
const WRITE_ROLES = ["pm", "sa", "leadership", "admin"];

/**
 * Roles that may ADD (Assign / Add) or SWAP a team member — POST .../resources and
 * POST .../resources/swap admit `sa`, `leadership` and `admin` only. `pm` was removed server-side
 * and keeps Remove only.
 */
const ADD_OR_SWAP_ROLES = ["sa", "leadership", "admin"];

/**
 * Roles offered by the catch-all "Other" selector: only those WITHOUT a section of their own.
 * Offering e.g. `Leadership` or `Project Manager` here would bypass the guarded-change rules of
 * those sections (T-29 / T-31).
 */
const OTHER_SECTION_ROLES = PROJECT_ROLE_VALUES.filter(
  (role) => !SECTIONED_ROLE_KEYS.has(role),
);

/** A stored role that the swap endpoint accepts (closed enum; legacy values are not swappable). */
function isSwappableRole(role: string): boolean {
  return (PROJECT_ROLE_VALUES as readonly string[]).includes(role);
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

/** "12.5" / "1,204" — at most 2 decimals, locale grouping. */
function formatHours(hours: number): string {
  return hours.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function errorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as AxiosError<{ message?: string }>;
  return (
    axiosErr?.response?.data?.message ??
    (err instanceof Error ? err.message : fallback)
  );
}

// ─── Card shell ────────────────────────────────────────────────────────────────

function CardShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary">
          <Users className="h-4 w-4 text-primary" aria-hidden="true" />
          Team &amp; Resources
        </h3>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// ─── Add-member dialog ─────────────────────────────────────────────────────────

/**
 * ONE dialog for all seven sections, made role-aware by `section`.
 *
 * The section's `roleKey` pre-selects the role and the role selector is hidden — "Add Engineer"
 * must not be able to post `Leadership`, or the row would land in a different section than the
 * button the user pressed. The catch-all ("Other", `roleKey: null`) is the exception: it SHOWS the
 * selector, because "Other" is where a user goes to pick a role that has no section of its own
 * (`QA Engineer`, `Designer`, `Business Analyst`, `Lead Engineer`, `Other`).
 */
function AddMemberDialog({
  projectId,
  section,
  open,
  onOpenChange,
}: {
  projectId: string;
  section: RoleSection;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const client = useApiClient();
  const queryClient = useQueryClient();

  /** Sections with a concrete roleKey lock the role; the catch-all lets the user choose. */
  const roleLocked = section.roleKey !== null;
  const initialRole = section.roleKey ?? DEFAULT_PROJECT_ROLE;

  const [selectedId, setSelectedId] = useState("");
  const [selectedName, setSelectedName] = useState("");
  const [roleOnProject, setRoleOnProject] = useState<string>(initialRole);
  const [formError, setFormError] = useState("");

  const assign = useMutation({
    mutationFn: async (): Promise<void> => {
      await client.post(`/api/projects/${projectId}/resources`, {
        resource_id: selectedId,
        // Always null — capacity allocation is out of MVP scope (Task #10). The backend accepts
        // null for every role and skips its overload guard. Never `?? 100` / `Number('')`.
        capacity_pct: null,
        // Never null — V043 (D) default is 'Other' and the Zod enum rejects null (BE-4).
        role_on_project: roleOnProject,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["project-resources", projectId],
      });
      toast.success("Team member added");
      reset();
      onOpenChange(false);
    },
    onError: (err) => {
      setFormError(errorMessage(err, "Failed to add team member"));
    },
  });

  function reset(): void {
    setSelectedId("");
    setSelectedName("");
    setRoleOnProject(initialRole);
    setFormError("");
  }

  /** Changing the resource invalidates any pending validation error for the previous selection. */
  function handleResourceChange(id: string, name: string): void {
    setSelectedId(id);
    setSelectedName(name);
    setFormError("");
  }

  function handleRoleChange(value: string): void {
    setRoleOnProject(value);
    setFormError("");
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError("");
    if (!selectedId) {
      setFormError("Select a resource to add.");
      return;
    }
    assign.mutate();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{section.addLabel}</DialogTitle>
          <DialogDescription>
            {roleLocked
              ? `Search the directory to add a ${projectRoleLabel(section.roleKey)} to this project.`
              : "Search the directory and choose a role."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="member-resource-picker">Resource</Label>
            <ResourcePicker
              id="member-resource-picker"
              value={selectedId}
              onChange={handleResourceChange}
              placeholder="Search by name or email…"
              // Directory filtering is unchanged and is derived from the ROLE, not from capacity:
              // named-lead roles (Leadership, PM, SA, …) are often people who do not submit
              // timesheets, so the `submits_timesheets` filter is relaxed for them; every other
              // role keeps the narrower assignable filter.
              requireAssignable={!isNamedLeadRole(roleOnProject)}
              // 2026-10-22: list only people whose title fits the role being filled (SA / PM /
              // Engineer — server-side). The section's locked role, or the role chosen in "Other".
              roleOnProject={roleOnProject}
            />
            {selectedId && (
              <p className="text-xs text-text-secondary">
                Selected:{" "}
                <span className="font-medium text-text-primary">
                  {selectedName}
                </span>
              </p>
            )}
          </div>

          {/*
            The role is fixed for the six concrete sections — an "Add Engineer" button that could
            post `Leadership` would file the row under a different section than the one the user
            pressed. The locked value is still shown, read-only, so the dialog is self-describing.
          */}
          {roleLocked ? (
            <div className="space-y-1.5">
              <p className="text-xs font-medium uppercase tracking-wide text-text-secondary">
                Role on project
              </p>
              {/*
                The DISPLAY label, not the raw enum. `roleKey` is still what gets POSTed — only the
                rendering is renamed, so this must not drift from the section heading above.
              */}
              <p className="text-sm font-medium text-text-primary">
                {projectRoleLabel(section.roleKey)}
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="member-role-on-project">Role on project</Label>
              <select
                id="member-role-on-project"
                className="flex h-9 w-full rounded-md border border-border bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                value={roleOnProject}
                onChange={(e) => handleRoleChange(e.target.value)}
              >
                {OTHER_SECTION_ROLES.map((role) => (
                  // `value` is the wire enum; the text is the display label. Never swap these.
                  <option key={role} value={role}>
                    {projectRoleLabel(role)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {formError && (
            <p
              id="add-member-error"
              className="text-sm text-error"
              role="alert"
            >
              {formError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={assign.isPending}>
              {assign.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              Add
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Member row ────────────────────────────────────────────────────────────────

/**
 * RES-01: `resources.name` can hold a raw Clockify account identifier rather than a person's
 * name — flag it instead of rendering it as a verified name. See frontend/src/lib/resource-name.ts.
 * Shared by the row and the remove-confirmation dialog so both name the person identically.
 */
function memberDisplayName(assignment: ProjectResource): ResourceDisplayName {
  return assignment.resource
    ? resolveResourceName(assignment.resource)
    : {
        label: assignment.resource_id,
        isFallback: true,
        identifier: assignment.resource_id,
        hint: MISSING_NAME_HINT,
      };
}

function MemberRow({
  assignment,
  showRole,
  canSwap,
  canRemove,
  removing,
  onSwap,
  onRemove,
}: {
  assignment: ProjectResource;
  /** The catch-all section holds mixed roles, so it labels each row with its own role. */
  showRole: boolean;
  /** Show the Swap action (write role AND a role the swap endpoint accepts). */
  canSwap: boolean;
  /** Show the Remove action (write role AND a `removable` section). */
  canRemove: boolean;
  removing: boolean;
  onSwap: () => void;
  onRemove: () => void;
}): JSX.Element {
  const displayName = memberDisplayName(assignment);

  const subtitle = showRole
    ? // Raw DB value — may be a deprecated legacy role, which `projectRoleLabel` passes through
      // unchanged so it stays readable (OQ-12).
      projectRoleLabel(assignment.role_on_project)
    : (assignment.resource?.designation ??
      assignment.resource?.role_title ??
      "—");

  return (
    <li className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p
          className={cn(
            "truncate",
            displayName.isFallback
              ? "italic text-text-secondary"
              : "font-medium text-text-primary",
          )}
          title={displayName.isFallback ? displayName.hint : undefined}
        >
          {displayName.label}
        </p>
        <p className="text-xs text-text-secondary">
          {subtitle}
          {(assignment.start_date || assignment.end_date) && (
            <>
              {" "}
              · {formatDate(assignment.start_date)} –{" "}
              {formatDate(assignment.end_date)}
            </>
          )}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {/*
          T-32 — Clockify hours on this project. The visible text is terse ("12.5 h" / "—"); the
          sr-only prefix gives screen readers the full meaning, and "—" is announced as "none"
          rather than read out as a dash.
        */}
        <p
          className="text-xs tabular-nums text-text-secondary"
          data-testid="member-clockify-hours"
          title="Hours logged in Clockify on this project"
        >
          <span className="sr-only">Hours logged in Clockify: </span>
          {assignment.clockify_hours != null ? (
            `${formatHours(assignment.clockify_hours)} h`
          ) : (
            <>
              <span aria-hidden="true">—</span>
              <span className="sr-only">none</span>
            </>
          )}
        </p>
        {/* No capacity badge — capacity allocation is out of MVP scope (Task #10). */}
        {canSwap && (
          <Button
            variant="ghost"
            size="sm"
            // Role included: one person can hold several rows, and a swap replaces one role only.
            aria-label={`Swap ${displayName.label} (${projectRoleLabel(assignment.role_on_project)})`}
            onClick={onSwap}
          >
            <ArrowLeftRight className="h-4 w-4" aria-hidden="true" />
            Swap
          </Button>
        )}
        {canRemove && (
          <Button
            variant="ghost"
            size="sm"
            // Role included: one person can hold several rows (e.g. PM + Engineer), and Remove
            // now removes only this row's role (D-8).
            aria-label={`Remove ${displayName.label} as ${projectRoleLabel(assignment.role_on_project)}`}
            disabled={removing}
            onClick={onRemove}
          >
            {removing ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            )}
            Remove
          </Button>
        )}
      </div>
    </li>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function UnifiedTeamSection({
  projectId,
}: UnifiedTeamSectionProps): JSX.Element {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canAddOrSwap = ADD_OR_SWAP_ROLES.includes(user?.role ?? "");
  const canRemove = WRITE_ROLES.includes(user?.role ?? "");

  /** Which section's Add dialog is open, by section id. One dialog instance at a time. */
  const [addingSectionId, setAddingSectionId] = useState<string | null>(null);
  /** `project_resources.id` of the row being removed — rows, not people (D-8). */
  const [removingId, setRemovingId] = useState<string | null>(null);
  /** The row awaiting "Remove {name} as {role}?" confirmation. */
  const [pendingRemoval, setPendingRemoval] = useState<ProjectResource | null>(
    null,
  );
  /** Section whose "Add … mid-project?" confirmation is open (addMode "confirm"). */
  const [confirmAddSectionId, setConfirmAddSectionId] = useState<string | null>(
    null,
  );
  /** The row being swapped (T-29 / T-31). */
  const [swapping, setSwapping] = useState<ProjectResource | null>(null);

  // Query key unchanged from ResourceSection — other surfaces invalidate on this exact key.
  const query = useQuery({
    queryKey: ["project-resources", projectId],
    queryFn: async (): Promise<ProjectResource[]> => {
      const response = await client.get<ProjectResource[]>(
        `/api/projects/${projectId}/resources`,
      );
      return response.data;
    },
    enabled: Boolean(projectId),
  });

  /**
   * D-8: always role-scoped. `?role=` makes the backend delete only this
   * (project, resource, role_on_project) row, so removing someone's Engineer row keeps their PM row.
   * The un-scoped DELETE (every role) is legacy and logged server-side for retirement.
   * encodeURIComponent, not URLSearchParams: roles contain spaces and `+` is not decoded to a space
   * by API Gateway.
   */
  const remove = useMutation({
    mutationFn: async (assignment: ProjectResource): Promise<void> => {
      await client.delete(
        `/api/projects/${projectId}/resources/${assignment.resource_id}?role=${encodeURIComponent(assignment.role_on_project)}`,
      );
    },
    onMutate: (assignment: ProjectResource) => setRemovingId(assignment.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["project-resources", projectId],
      });
      toast.success("Team member removed");
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Failed to remove team member"));
    },
    onSettled: () => setRemovingId(null),
  });

  const assignments = query.data ?? [];

  /**
   * Bucket every assignment into exactly one section.
   *
   * Built from `ROLE_SECTIONS` rather than from the data so empty sections still render (they are
   * where the Add button lives) and section order is stable regardless of what the API returns.
   */
  const bySection = useMemo(() => {
    const buckets = new Map<string, ProjectResource[]>(
      ROLE_SECTIONS.map((s) => [s.id, [] as ProjectResource[]]),
    );
    for (const a of assignments) {
      // Unrecognised and deprecated-legacy roles fall through to 'other' — they must stay visible
      // on existing projects even though no picker offers them (OQ-12).
      buckets.get(sectionIdForRole(a.role_on_project))!.push(a);
    }
    return buckets;
  }, [assignments]);

  const activeSection =
    ROLE_SECTIONS.find((s) => s.id === addingSectionId) ?? null;
  const confirmAddSection =
    ROLE_SECTIONS.find((s) => s.id === confirmAddSectionId) ?? null;

  /** Open the add flow for a section: confirm first when the section asks for it. */
  function startAdd(section: RoleSection): void {
    if (section.addMode === "confirm") setConfirmAddSectionId(section.id);
    else setAddingSectionId(section.id);
  }

  // Loading.
  if (query.isLoading) {
    return (
      <CardShell>
        <div className="space-y-3" role="status" aria-label="Loading team">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </CardShell>
    );
  }

  // Error.
  if (query.isError) {
    return (
      <CardShell>
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-error">Failed to load team.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void query.refetch()}
          >
            Retry
          </Button>
        </div>
      </CardShell>
    );
  }

  return (
    <>
      <CardShell>
        {/* Whole-card empty state — distinct from a per-section empty label. */}
        {assignments.length === 0 && (
          <p className="mb-6 text-sm text-text-secondary">
            No team members assigned.
            {canAddOrSwap &&
              " Use the Assign and Add actions below to put people on this project."}
          </p>
        )}

        <div className="space-y-6">
          {ROLE_SECTIONS.map((section) => {
            const rows = bySection.get(section.id) ?? [];
            const headingId = `team-section-${section.id}-heading`;
            // T-29 / T-31: single-holder roles get "Assign" only while empty; Leadership / AE none.
            const showAdd =
              canAddOrSwap &&
              (section.addMode === "confirm" ||
                (section.addMode === "assign-if-empty" && rows.length === 0));
            return (
              <section key={section.id} aria-labelledby={headingId}>
                <div className="flex items-center justify-between gap-2 border-b border-outlines pb-1.5">
                  <h4
                    id={headingId}
                    className="text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    {section.label}
                  </h4>
                  {showAdd && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startAdd(section)}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      {section.addLabel}
                    </Button>
                  )}
                </div>

                {rows.length === 0 ? (
                  <p className="pt-2.5 text-sm text-text-secondary">
                    {section.emptyLabel}
                  </p>
                ) : (
                  <ul className="divide-y divide-outlines pt-2.5">
                    {rows.map((a) => (
                      <MemberRow
                        key={a.id}
                        assignment={a}
                        // Only the catch-all mixes roles, so only it needs a per-row role label.
                        showRole={section.roleKey === null}
                        canSwap={
                          canAddOrSwap && isSwappableRole(a.role_on_project)
                        }
                        canRemove={canRemove && section.removable}
                        removing={remove.isPending && removingId === a.id}
                        onSwap={() => setSwapping(a)}
                        // Ask first (D-8) — the confirm dialog fires the mutation.
                        onRemove={() => setPendingRemoval(a)}
                      />
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      </CardShell>

      {/*
        One dialog, keyed by section id. The key forces a remount when the user switches sections so
        the form's initial role state is re-seeded from the new section rather than
        carrying over the previous section's values.
      */}
      {canAddOrSwap && activeSection && (
        <AddMemberDialog
          key={activeSection.id}
          projectId={projectId}
          section={activeSection}
          open
          onOpenChange={(next) => {
            if (!next) setAddingSectionId(null);
          }}
        />
      )}

      {canAddOrSwap && (
        <AddMidProjectConfirm
          section={confirmAddSection}
          onCancel={() => setConfirmAddSectionId(null)}
          onConfirm={(section) => {
            setConfirmAddSectionId(null);
            setAddingSectionId(section.id);
          }}
        />
      )}

      {/* Mounted only while open, so its mutation hook never runs for read-only viewers. */}
      {canAddOrSwap && swapping && (
        <SwapResourceDialog
          key={swapping.id}
          projectId={projectId}
          target={{
            resourceId: swapping.resource_id,
            name: memberDisplayName(swapping).label,
            role: swapping.role_on_project,
          }}
          open
          onOpenChange={(next) => {
            if (!next) setSwapping(null);
          }}
          onSwapped={(replacementName) =>
            toast.success(
              replacementName
                ? `${replacementName} is now ${projectRoleLabel(swapping.role_on_project)}`
                : "Team member swapped",
            )
          }
        />
      )}

      {canRemove && (
        <RemoveMemberConfirm
          assignment={pendingRemoval}
          onCancel={() => setPendingRemoval(null)}
          onConfirm={(assignment) => {
            setPendingRemoval(null);
            remove.mutate(assignment);
          }}
        />
      )}
    </>
  );
}

// ─── Add mid-project confirmation (T-29 / T-31) ────────────────────────────────

/**
 * "Add an engineer mid-project?" — shown before the Add dialog for `addMode: "confirm"` sections,
 * because a new member is granted the project's Slack / Jira / GitHub / SharePoint access.
 * Radix AlertDialog: focus trap, Esc-to-cancel, initial focus on Cancel.
 */
function AddMidProjectConfirm({
  section,
  onCancel,
  onConfirm,
}: {
  section: RoleSection | null;
  onCancel: () => void;
  onConfirm: (section: RoleSection) => void;
}): JSX.Element {
  const lastShown = useRef<RoleSection | null>(null);
  if (section) lastShown.current = section;
  const shown = section ?? lastShown.current;

  return (
    <AlertDialog
      open={section !== null}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{shown?.addConfirm?.title ?? ""}</AlertDialogTitle>
          <AlertDialogDescription>
            {shown?.addConfirm?.description ?? ""}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (section) onConfirm(section);
            }}
          >
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Remove confirmation (D-8) ─────────────────────────────────────────────────

/**
 * "Remove {name} as {role}?" — removal is per ROLE ROW, so the role is always named: a person who
 * is both PM and Engineer must be able to tell which of their two rows they are about to delete.
 * Radix AlertDialog gives focus trap, Esc-to-cancel and initial focus on Cancel (the safe choice).
 */
function RemoveMemberConfirm({
  assignment,
  onCancel,
  onConfirm,
}: {
  assignment: ProjectResource | null;
  onCancel: () => void;
  onConfirm: (assignment: ProjectResource) => void;
}): JSX.Element {
  // Keep the last row's text while the close animation plays, so the title never flashes to
  // "Remove  as ?" after Cancel/Remove clears the pending row.
  const lastShown = useRef<ProjectResource | null>(null);
  if (assignment) lastShown.current = assignment;
  const shown = assignment ?? lastShown.current;
  const name = shown ? memberDisplayName(shown).label : "";
  const role = shown ? projectRoleLabel(shown.role_on_project) : "";

  return (
    <AlertDialog
      open={assignment !== null}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{`Remove ${name} as ${role}?`}</AlertDialogTitle>
          <AlertDialogDescription>
            {`${name} will no longer be listed as ${role} on this project. Any other roles they hold here are kept.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant="danger"
              onClick={() => {
                if (assignment) onConfirm(assignment);
              }}
            >
              Remove
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default UnifiedTeamSection;
